import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PageMeta from "@/components/PageMeta";
import { useLanguage } from "@/i18n/LanguageContext";

const Blog = () => {
    const { t } = useLanguage();
    const B = t.blog;
    const seo = t.page_meta.seo.blog;

    useEffect(() => {
        window.scrollTo({ top: 0 });
    }, []);

    return (
        <div data-testid="blog-page" className="bg-ella-cream min-h-screen">
            <Header />
            <PageMeta title={seo.title} h1={seo.h1} description={seo.description} />
            <main className="pt-32 md:pt-40 pb-24">
                <div className="ella-container">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-ella-muted hover:text-ella-ink transition-colors"
                        data-testid="blog-back-home"
                    >
                        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
                        {t.legal.back}
                    </Link>

                    <div className="mt-12 max-w-3xl">
                        <span className="ella-overline" data-testid="blog-list-overline">
                            {B.overline}
                        </span>
                        <h2
                            className="ella-headline text-4xl md:text-5xl lg:text-6xl mt-4 text-ella-ink"
                            data-testid="blog-list-title"
                        >
                            {B.list_title}
                        </h2>
                        <p className="mt-6 text-ella-ink/70 leading-relaxed font-light text-lg">
                            {B.list_subtitle}
                        </p>
                    </div>

                    <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blog-grid">
                        {(B.posts || []).map((post) => (
                            <Link
                                key={post.slug}
                                to={`/journal/${post.slug}`}
                                data-testid={`blog-list-card-${post.slug}`}
                                className="group flex flex-col bg-white border border-ella-line rounded-3xl overflow-hidden hover:shadow-[0_24px_60px_rgba(31,29,28,0.12)] hover:border-ella-gold/40 transition-all duration-500"
                            >
                                <div className="relative aspect-[16/10] overflow-hidden">
                                    <img
                                        src={post.image}
                                        alt={post.title}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                    <div className="absolute top-4 left-4 px-3 py-1 bg-ella-cream text-ella-ink text-[10px] uppercase tracking-[0.24em] rounded-full">
                                        {post.category}
                                    </div>
                                </div>
                                <div className="flex flex-col flex-1 p-7">
                                    <div className="flex items-center gap-3 text-[11px] text-ella-muted uppercase tracking-[0.2em]">
                                        <span>{post.date}</span>
                                        <span className="text-ella-gold/60">·</span>
                                        <span className="inline-flex items-center gap-1">
                                            <Clock className="w-3 h-3" strokeWidth={1.5} />
                                            {post.read_minutes} {B.min_read}
                                        </span>
                                    </div>
                                    <h2 className="font-display text-2xl md:text-[26px] mt-4 text-ella-ink leading-tight group-hover:text-ella-gold-dark transition-colors">
                                        {post.title}
                                    </h2>
                                    <p className="mt-4 text-ella-ink/70 font-light leading-relaxed text-[15px] flex-1">
                                        {post.excerpt}
                                    </p>
                                    <div className="mt-6 inline-flex items-center gap-2 text-ella-gold-dark text-xs uppercase tracking-[0.24em]">
                                        {B.cta_read}
                                        <ArrowRight
                                            className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform"
                                            strokeWidth={1.5}
                                        />
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default Blog;
